import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { ClaimFolder } from "@/api/generated/client/models/ClaimFolder";
import { AlertCircle, FolderTree, Loader2, Pencil, Plus, Trash2 } from "lucide-react";

interface ClaimFoldersSectionProps {
  folders: ClaimFolder[];
  isLoading: boolean;
  error?: Error | null;
  canManageAllClaimFolders: boolean;
  currentUserId?: number;
  isCreating: boolean;
  onRetry?: () => void;
  setCreateDialogOpen: (open: boolean) => void;
  setEditingFolder: (folder: ClaimFolder | null) => void;
  setEditDialogOpen: (open: boolean) => void;
  setFolderToDelete: (folder: ClaimFolder | null) => void;
  setDeleteDialogOpen: (open: boolean) => void;
}

export function ClaimFoldersSection({
  folders,
  isLoading,
  error,
  canManageAllClaimFolders,
  currentUserId,
  isCreating,
  onRetry,
  setCreateDialogOpen,
  setEditingFolder,
  setEditDialogOpen,
  setFolderToDelete,
  setDeleteDialogOpen,
}: ClaimFoldersSectionProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FolderTree className="h-5 w-5" />
              Claim Folders
              {folders.length > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {folders.length}
                </Badge>
              )}
            </CardTitle>
            <CardDescription className="mt-1.5">
              Create folders to group and organize claims.
            </CardDescription>
          </div>
          <Button
            size="sm"
            onClick={() => setCreateDialogOpen(true)}
            disabled={isCreating}
            className="w-full sm:w-auto"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Create Folder
          </Button>
        </div>
      </CardHeader>

      {isLoading && (
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </CardContent>
      )}

      {!isLoading && error && (
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error Loading Claim Folders</AlertTitle>
            <AlertDescription className="mt-2 flex items-center justify-between gap-3">
              <span>{error instanceof Error ? error.message : "Failed to load claim folders"}</span>
              {onRetry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                  Retry
                </Button>
              )}
            </AlertDescription>
          </Alert>
        </CardContent>
      )}

      {!isLoading && !error && folders.length > 0 && (
        <CardContent>
          <div className="space-y-2">
            {folders.map((folder) => (
              <div key={folder.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="flex min-w-0 items-center gap-2">
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full border"
                    style={{ backgroundColor: folder.color ?? "#94A3B8" }}
                    aria-hidden="true"
                  />
                  <span className="truncate">{folder.name}</span>
                </span>
                <span className="flex shrink-0 items-center gap-1">
                  {(canManageAllClaimFolders || folder.createdBy === currentUserId) && (
                    <>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        aria-label={`Edit ${folder.name}`}
                        onClick={() => {
                          setEditingFolder(folder);
                          setEditDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        aria-label={`Delete ${folder.name}`}
                        onClick={() => {
                          setFolderToDelete(folder);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </span>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
